import { BaseStore } from './baseStore';
import { Store } from './store';
import { action, makeObservable, observable } from 'mobx';
import { SessionStoreState } from './SessionStore';

export enum UserStoreState {
  NOT_INITIALIZED = 1 << 0,
  RECEIVED_USER = 1 << 1,
  RELATED_USER = 1 << 2,
}

export class UserStore extends BaseStore<UserStoreState> {
  user: any;
  related: any[];

  constructor(store: Store) {
    super(UserStoreState.NOT_INITIALIZED, store, 'yellow');
    makeObservable(this, {
      user: observable,
      related: observable,
      updateUser: action,
      updateRelatedUser: action,
      setUser: action,
      setRelatedUser: action,
    });
    this.log('Starting loading UserStore');
  }

  reload(): void {
    this.user = null;
    this.related = [];
    this.setState(UserStoreState.NOT_INITIALIZED);

    if (this.store?.token) {
      this.loadUserData();
    }
  }

  async loadUserData(): Promise<void> {
    try {
      this.log('Loading user data from API...');

      const response = await fetch(`${this.store!.apiURL}/user/@me`, {
        headers: { Authorization: `Bearer ${this.store!.token}` },
      });

      if (response.status === 401) {
        this.log('Unauthorized while loading user data; logging out');
        this.store!.logout();
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to load user data: ${response.statusText}`);
      }

      const responseData = await response.json();
      const userData = responseData.data || responseData;
      this.setUser(userData);
      this.log('User data loaded successfully');
      this.store.socket.ws.on('user:related', data => {
        this.log('Received related user from socket', data);
        this.store!.user.setRelatedUser(data);
      });
      this.store.socket.ws.on('client:update', data => {
        this.log('Received client:update from socket:', data);
        this.store.user.updateUser(data);
      });

      this.store.socket.ws.on('user:update', data => {
        this.log('Received user:update from socket:', data);
        this.store.user.updateRelatedUser(data);
      });
    } catch (error) {
      this.log(`Failed to load user data: ${error}`);
    }
  }

  setUser(data: any) {
    this.log('User is logged');
    this.user = data;
    if (data == null) {
      this.removeState(UserStoreState.RECEIVED_USER);
    } else {
      this.addState(UserStoreState.RECEIVED_USER);
    }
  }

  updateUser(data: any) {
    this.log('Updating user data locally:', data);
    this.user = Object.assign({}, this.user, data);
  }

  updateRelatedUser(data: any) {
    let user = this.related.find(user => user.id === data.id);
    if (user) {
      user = Object.assign(user, data);
    }
  }

  setRelatedUser(data: any) {
    this.related = data;
    if (data == null) {
      this.removeState(UserStoreState.RELATED_USER);
    } else {
      this.addState(UserStoreState.RELATED_USER);
    }
  }

  isReady(): boolean {
    return (
      this.hasState(UserStoreState.RECEIVED_USER) && this.hasState(UserStoreState.RELATED_USER)
    );
  }

  logout(): void {
    this.user = null;
    this.related = [];
    this.setState(UserStoreState.NOT_INITIALIZED);
  }

  async updateUserData(id = '@me', data: any): Promise<void> {
    try {
      this.log('Updating user data:', data);

      const response = await fetch(`${this.store!.apiURL}/user/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.store!.token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Update failed: ${error}`);
      }

      const responseData = await response.json();
      const serverData = responseData.data || responseData;

      if (serverData.success === true && !serverData.status) {
        if (id === '@me') {
          this.updateUser(data);
          this.log('Optimistic update applied');
        }
        return;
      }

      if (id === '@me') {
        this.updateUser(serverData);
        this.log('User updated with server data');
      } else {
        this.updateRelatedUser({ id, ...serverData });
        this.log('Related user updated');
      }
    } catch (error) {
      this.log(`Update failed:`, error);
      throw error;
    }
  }

  enableTwoFactoryMode() {
    fetch(`${this.store!.apiURL}/auth/2fa`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.store!.token}`,
      },
    }).then(async response => {
      this.log(response);
    });
  }

  disableTwoFactoryMode() {
    fetch(`${this.store!.apiURL}/auth/2fa`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${this.store!.token}`,
      },
    }).then(async response => {
      this.log(response);
    });
  }

  getTwoFactoryMode() {
    return fetch(`${this.store!.apiURL}/auth/2fa`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.store!.token}`,
      },
    }).then(async response => {
      return await response.text();
    });
  }

  validate(code: string) {
    return fetch(`${this.store!.apiURL}/auth/2fa/validate?code=${code}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${this.store!.token}`,
      },
    }).then(async response => {
      return await response.json();
    });
  }
}
